import React, { useState, useRef, useEffect } from 'react';
import {
    Container,
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Link,
    Stack,
    CircularProgress,
    InputAdornment,
    IconButton
} from '@mui/material';
import { ArrowBack, Visibility, VisibilityOff } from '@mui/icons-material';
import logo from '../../assets/xpdlogo.png';
import { styled } from '@mui/material/styles';
import toast from 'react-hot-toast'; // Make sure to import this
// Styled OTP Input
const OtpInput = styled(TextField)(({ theme }) => ({
    width: '50px',
    '& .MuiOutlinedInput-root': {
        '& input': {
            padding: '12px',
            textAlign: 'center',
            fontSize: '1.2rem',
            fontWeight: '500',
        },
        '& fieldset': {
            borderColor: '#e0e0e0',
            borderRadius: '8px',
        },
        '&:hover fieldset': {
            borderColor: '#542374',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#542374',
        },
    },
    [theme.breakpoints.down('sm')]: {
        width: '40px',
        '& .MuiOutlinedInput-root': {
            '& input': {
                padding: '10px',
                fontSize: '1rem',
            },
        },
    },
}));
const ForgotPassword = () => {
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [emailSent, setEmailSent] = useState(false);
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordData, setPasswordData] = useState({
        newPassword: '',
        confirmPassword: ''
    });
    const inputRefs = useRef([]);

    // Handle OTP input change
    const handleChange = (index, value) => {
        if (isNaN(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        // Move to next input if value exists
        if (value !== '' && index < 5) {
            inputRefs.current[index + 1].focus();
        }
    };

    // Handle backspace
    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1].focus();
        }
    };

    // Handle paste
    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').slice(0, 6).split('');
        if (pastedData.some(x => isNaN(x))) return;

        const newOtp = [...otp];
        pastedData.forEach((value, index) => {
            if (index < 6) newOtp[index] = value;
        });
        setOtp(newOtp);
    };
    // Handle password change
    const handlePasswordChange = (field) => (event) => {
        setPasswordData({
            ...passwordData,
            [field]: event.target.value
        });
    };
    // Handle verification
    const handleVerify = async () => {
        const otpString = otp.join('');
        if (otpString.length !== 6) {
            toast.error('Please enter complete OTP');
            return;
        }

        setIsSubmitting(true);
        try {
            // Your API call here
            // const response = await verifyOtp(otpString);
            toast.success('Email verified successfully');
            // Handle successful verification
        } catch (error) {
            toast.error(error.message || 'Verification failed');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Add these states to your component
    const [resendTimer, setResendTimer] = useState(30); // 30 seconds countdown
    const [canResend, setCanResend] = useState(false);

    // Add this useEffect for the countdown timer
    useEffect(() => {
        let interval;
        if (resendTimer > 0) {
            interval = setInterval(() => {
                setResendTimer((prev) => prev - 1);
            }, 1000);
        } else {
            setCanResend(true);
        }

        return () => clearInterval(interval);
    }, [resendTimer]);

    // Add this function to handle resend
    // First, add this at the top of your file with other imports
    const API_URL =  'https://sandbox.myxpd.app/api';

    // Updated handleSubmit function for sending OTP
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsSubmitting(true);

            if (!email) {
                toast.error('Please enter your email address');
                return;
            }

            const response = await fetch(`/api/send-otp`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to send OTP');
            }

            setEmailSent(true);
            toast.success('OTP sent successfully');
            // Start the resend timer
            setResendTimer(30);
            setCanResend(false);

        } catch (error) {
            toast.error(error.message || 'Failed to send OTP');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Updated handleChangePasswordSubmit function
    const handleChangePasswordSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsSubmitting(true);

            const otpString = otp.join('');
            // Client-side validations
            if (otpString.length !== 6) {
                toast.error('Please enter complete OTP');
                return;
            }

            if (!passwordData.newPassword || !passwordData.confirmPassword) {
                toast.error('Please enter all password fields');
                return;
            }

            if (passwordData.newPassword !== passwordData.confirmPassword) {
                toast.error('Passwords do not match');
                return;
            }

            if (passwordData.newPassword.length < 8) {
                toast.error('Password must be at least 8 characters long');
                return;
            }

            // Password strength validation
            const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
            if (!passwordRegex.test(passwordData.newPassword)) {
                toast.error('Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character');
                return;
            }

            const response = await fetch(`/api/reset-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email,
                    otp: otpString,
                    newPassword: passwordData.newPassword
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to reset password');
            }

            toast.success('Password changed successfully');
            // Redirect to login page after successful password change
            setTimeout(() => {
                window.location.href = '/login';
            }, 2000);

        } catch (error) {
            toast.error(error.message || 'Failed to change password');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Updated handleResendOTP function
    const handleResendOTP = async () => {
        try {
            setIsSubmitting(true);

            const response = await fetch(`/api/resend-otp`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to resend OTP');
            }

            // Reset timer and states
            setResendTimer(30);
            setCanResend(false);
            toast.success('OTP resent successfully');

        } catch (error) {
            toast.error(error.message || 'Failed to resend OTP');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Add this utility function for email validation
    const isValidEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };


    return (
        <Container maxWidth="sm">
            <Box
                sx={{
                    mt: { xs: 4, sm: 8 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    minHeight: '100vh',
                }}
            >
                <Paper
                    elevation={2}
                    sx={{
                        p: { xs: 2, sm: 3 },
                        width: '100%',
                        maxWidth: '400px',
                        borderRadius: '12px',
                        mb: 4,
                        bgcolor: '#ffffff',
                    }}
                >
                    {/* Logo */}
                    <Box sx={{ textAlign: 'center' }}>
                        <img
                            src={logo}
                            alt="XPD Logo"
                            style={{
                                width: 'auto',
                                height: '60px',
                                marginBottom: '24px'
                            }}
                        />
                    </Box>


                    {!emailSent ? (
                        <>
                            <Typography
                                variant="h6"
                                sx={{
                                    color: '#000000',
                                    mb: 1,
                                    textAlign: 'center',
                                    fontSize: { xs: '1.1rem', sm: '1.25rem' }
                                }}
                            >
                                Forgot Password?
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: '#666666',
                                    mb: 4,
                                    textAlign: 'center',
                                    fontSize: { xs: '0.875rem', sm: '1rem' }
                                }}
                            >
                                Enter your email address and we'll send you instructions to reset your password.
                            </Typography>

                            <form onSubmit={handleSubmit}>
                                <Box sx={{ mb: 3 }}>
                                    <TextField
                                        label="Email Address"
                                        variant="outlined"
                                        fullWidth
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        type="email"
                                        required
                                        size="small"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: '#000000',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#666666',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#542374',
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: '#666666',
                                                '&.Mui-focused': {
                                                    color: '#542374',
                                                },
                                            },
                                        }}
                                    />
                                </Box>

                                <Button
                                    type="submit"
                                    variant="contained"
                                    fullWidth
                                    disabled={isSubmitting}
                                    sx={{
                                        py: { xs: 1, sm: 1.5 },
                                        bgcolor: '#000000', // Changed to black
                                        color: '#ffffff',
                                        '&:hover': {
                                            bgcolor: '#333333', // Slightly lighter black on hover
                                        },
                                        '&.Mui-disabled': {
                                            bgcolor: 'rgba(0, 0, 0, 0.7)',
                                            color: 'rgba(255, 255, 255, 0.8)'
                                        },
                                        fontWeight: 600,
                                        fontSize: { xs: '0.875rem', sm: '0.9rem' },
                                        textTransform: 'none',
                                        borderRadius: '8px',
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)', // Adjusted shadow for black button
                                        display: 'flex',
                                        gap: 1,
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <CircularProgress
                                                size={24}
                                                sx={{
                                                    color: '#ffffff'
                                                }}
                                            />
                                            <span>Processing...</span>
                                        </>
                                    ) : (
                                        'Reset Password' // Changed button text
                                    )}
                                </Button>
                                {/* Back to Login Link */}
                                <Box
                                    sx={{
                                        mt: 3,
                                        display: 'flex',
                                        justifyContent: 'center', // Centers horizontally
                                        alignItems: 'center',     // Centers vertically
                                        width: '100%'            // Ensures full width for proper centering
                                    }}
                                >
                                    <Link
                                        href="/login"
                                        sx={{
                                            color: '#542374',
                                            textDecoration: 'none',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            '&:hover': {
                                                color: '#000000',
                                            },
                                            fontSize: '0.875rem'
                                        }}
                                    >
                                        <ArrowBack sx={{ fontSize: '1rem' }} />
                                        Back to Login
                                    </Link>
                                </Box>


                            </form>
                        </>
                    ) : (
                        // Success Message
                        <Box sx={{ textAlign: 'center', py: 2, px: 3 }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    color: '#542374',
                                    mb: 2,
                                    fontSize: { xs: '1.1rem', sm: '1.25rem' }
                                }}
                            >
                                Change Password
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: '#666666',
                                    mb: 3,
                                    fontSize: { xs: '0.875rem', sm: '1rem' }
                                }}
                            >
                                Enter the OTP sent to your email and set your new password
                            </Typography>

                            {/* OTP Input Fields */}
                            <Stack
                                direction="row"
                                spacing={{ xs: 1, sm: 2 }}
                                justifyContent="center"
                                mb={3}
                            >
                                {otp.map((digit, index) => (
                                    <OtpInput
                                        key={index}
                                        inputRef={(ref) => inputRefs.current[index] = ref}
                                        value={digit}
                                        onChange={(e) => handleChange(index, e.target.value)}
                                        onKeyDown={(e) => handleKeyDown(index, e)}
                                        onPaste={handlePaste}
                                        inputProps={{
                                            maxLength: 1,
                                            inputMode: 'numeric',
                                            pattern: '[0-9]*'
                                        }}
                                    />
                                ))}
                            </Stack>

                            {/* Password Fields */}
                            <Stack spacing={2} mb={3}>
                                <TextField
                                    fullWidth
                                    label="New Password"
                                    type={showNewPassword ? 'text' : 'password'}
                                    value={passwordData.newPassword}
                                    onChange={handlePasswordChange('newPassword')}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                                    edge="end"
                                                >
                                                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: '#e0e0e0',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: '#542374',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#542374',
                                            },
                                        },
                                    }}
                                />

                                <TextField
                                    fullWidth
                                    label="Confirm Password"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    value={passwordData.confirmPassword}
                                    onChange={handlePasswordChange('confirmPassword')}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    edge="end"
                                                >
                                                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: '#e0e0e0',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: '#542374',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#542374',
                                            },
                                        },
                                    }}
                                />
                            </Stack>

                            {/* Change Password Button */}
                            <Button
                                variant="contained"
                                fullWidth
                                disabled={isSubmitting || otp.some(digit => !digit) || !passwordData.newPassword || !passwordData.confirmPassword}
                                onClick={handleChangePasswordSubmit}
                                sx={{
                                    bgcolor: '#542374',
                                    '&:hover': {
                                        bgcolor: '#7B3AA0'
                                    },
                                    '&.Mui-disabled': {
                                        bgcolor: 'rgba(84, 35, 116, 0.7)',
                                    },
                                    textTransform: 'none',
                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                    py: { xs: 1.5, sm: 1.75 }
                                }}
                            >
                                {isSubmitting ? (
                                    <>
                                        <CircularProgress
                                            size={20}
                                            sx={{
                                                color: 'white',
                                                marginRight: 1
                                            }}
                                        />
                                        Changing Password...
                                    </>
                                ) : (
                                    'Change Password'
                                )}
                            </Button>

                            {/* Resend OTP */}
                            <Box sx={{ mt: 2 }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: '#666666',
                                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 0.5
                                    }}
                                >
                                    Didn't receive the code?
                                    {canResend ? (
                                        <Button
                                            onClick={handleResendOTP}
                                            disabled={isSubmitting}
                                            sx={{
                                                color: '#542374',
                                                textTransform: 'none',
                                                p: 0,
                                                minWidth: 'auto',
                                                fontSize: 'inherit',
                                                fontWeight: 500,
                                                '&:hover': {
                                                    bgcolor: 'transparent',
                                                    textDecoration: 'underline'
                                                }
                                            }}
                                        >
                                            {isSubmitting ? (
                                                <CircularProgress
                                                    size={12}
                                                    sx={{
                                                        color: '#542374',
                                                        ml: 1
                                                    }}
                                                />
                                            ) : (
                                                'Resend'
                                            )}
                                        </Button>
                                    ) : (
                                        <Typography
                                            component="span"
                                            sx={{
                                                color: '#542374',
                                                fontSize: 'inherit',
                                                fontWeight: 500,
                                                ml: 0.5
                                            }}
                                        >
                                            Resend in {resendTimer}s
                                        </Typography>
                                    )}
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {/* Footer */}
                    <Box sx={{
                        mt: 4,
                        pt: 3,
                        borderTop: '1px solid #e0e0e0',
                        textAlign: 'center'
                    }}>
                        {/* <Box sx={{ mb: 1 }}>
                            <Link
                                href="https://myxpd.app/terms-of-use"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    color: '#542374',
                                    textDecoration: 'none',
                                    mx: 1,
                                    fontSize: '0.8rem',
                                    '&:hover': { color: '#000000' }
                                }}
                            >
                                Terms & Conditions
                            </Link>
                            <Link
                                href="https://myxpd.app/privacy-policy"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    color: '#542374',
                                    textDecoration: 'none',
                                    mx: 1,
                                    fontSize: '0.8rem',
                                    '&:hover': { color: '#000000' }
                                }}
                            >
                                Privacy
                            </Link>
                            <Link
                                href="https://myxpd.app/cookies"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    color: '#542374',
                                    textDecoration: 'none',
                                    mx: 1,
                                    fontSize: '0.8rem',
                                    '&:hover': { color: '#000000' }
                                }}
                            >
                                Cookies
                            </Link>
                        </Box> */}
                        <Typography
                            variant="body2"
                            sx={{
                                color: '#542374',
                                fontSize: '0.8rem'
                            }}
                        >
                            <p>© 2024–{new Date().getFullYear()} XPD. All Rights Reserved.</p>
                        </Typography>
                    </Box>
                </Paper>
            </Box>
        </Container>
    );
};

export default ForgotPassword;
